import React, { type ReactNode, useMemo } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import { fg } from '@atlassian/jira-feature-gating';
import { DEFAULT_VIEW_ID } from '../../common/constants.tsx';
import type { IssueNavigatorViewId } from '../../common/types.tsx';
import { convertToView, parseIssueNavigatorViewIdOrDefault } from '../../common/utils/index.tsx';
import { Container as SelectedViewStateContainer } from '../../controllers/selected-view-state/index.tsx';
import { useIssueSearchActions } from '../../services/issue-search/selectors.tsx';
import type { selectedView_issueNavigator_SelectedViewContainer$key as JiraViewFragment } from './__generated__/selectedView_issueNavigator_SelectedViewContainer.graphql';

type IssueKey = string;

export type Props = {
	children: ReactNode;
	jiraView: JiraViewFragment | null;
	/**
	 * Event emitted when the selected view is updated. The caller may want to specify the issue which should be
	 * selected by default when the view is changed, in which case they can provide an `issueKey` argument.
	 *
	 * @param view View that was updated
	 * @param issueKey Optional issue key that should be selected
	 */
	onSetView: (view: IssueNavigatorViewId, issueKey: IssueKey) => void;
};

export const SelectedViewContainer = ({ children, jiraView, onSetView }: Props) => {
	const { changeView } = useIssueSearchActions();

	const jiraViewData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment selectedView_issueNavigator_SelectedViewContainer on JiraView {
				... on JiraIssueSearchViewMetadata {
					viewId
				}
			}
		`,
		jiraView,
	);

	const viewId = parseIssueNavigatorViewIdOrDefault(jiraViewData?.viewId, DEFAULT_VIEW_ID);
	const ufoData = useMemo(
		() =>
			fg('jira_nin_last_used_jql_tech_changes') ? { viewLayout: convertToView(viewId) } : undefined,
		[viewId],
	);

	return (
		<SelectedViewStateContainer
			view={viewId}
			onSetView={onSetView}
			onIssueSearchForView={changeView}
		>
			{children}
			{ufoData && fg('jira_nin_last_used_jql_tech_changes') && <UFOCustomData data={ufoData} />}
		</SelectedViewStateContainer>
	);
};
